// src/graphql/coresite.js
// Queries for SiteSettings, AboutPageSettings, AboutStats, CompanyValues, etc.
// Mutations: SubscribeNewsletter, SubmitContactEnquiry

import { gql } from '@apollo/client';

// ─────────────────────────────────────────────────────────────────────────────
// SITE SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_SITE_SETTINGS = gql`
  query GetSiteSettings {
    siteSettings {
      address
      city
      phone
      whatsapp
      primaryEmail
      website

      facebookUrl
      instagramUrl
      twitterUrl
      linkedinUrl

      establishedLabel
      brandStatement
      newsletterTagline

      quickLinks {
        id
        label
        path
        sortOrder
      }
      trustBadges {
        id
        icon
        label
        sub
        sortOrder
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// ABOUT PAGE SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_ABOUT_PAGE_SETTINGS = gql`
  query GetAboutPageSettings {
    aboutPageSettings {
      heroImage
      heroEyebrow
      heroHeadingLine1
      heroHeadingLine2
      heroBody
      storyImage
      storyEyebrow
      storyHeading
      storyBody1
      storyBody2
      pullQuote
      pullQuoteBy
      philosophyEyebrow
      philosophyHeading
      philosophySub
      milestonesEyebrow
      milestonesHeadingLine1
      milestonesHeadingLine2
      teamEyebrow
      teamHeading
      teamSub
      ctaEyebrow
      ctaHeading
      ctaBody
      travellersDisplay
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// ABOUT STATS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_ABOUT_STATS = gql`
  query GetAboutStats {
    aboutStats {
      totalTours
      totalCountries
      yearsOperating
      averageRating
      travellers
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY VALUES
// ─────────────────────────────────────────────────────────────────────────────

export const GET_COMPANY_VALUES = gql`
  query GetCompanyValues {
    companyValues {
      id
      icon
      title
      description
      sortOrder
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TEAM MEMBERS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_TEAM_MEMBERS = gql`
  query GetTeamMembers {
    teamMembers {
      id
      name
      role
      bio
      photo
      sortOrder
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY MILESTONES
// ─────────────────────────────────────────────────────────────────────────────

export const GET_COMPANY_MILESTONES = gql`
  query GetCompanyMilestones {
    companyMilestones {
      id
      year
      title
      description
      sortOrder
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// PRESS PARTNERS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_PRESS_PARTNERS = gql`
  query GetPressPartners {
    pressPartners {
      id
      name
      logoUrl
      sortOrder
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// COMBINED ABOUT PAGE  (single round-trip)
// ─────────────────────────────────────────────────────────────────────────────

export const GET_ABOUT_PAGE_ALL = gql`
  query GetAboutPageAll {
    aboutPageSettings {
      heroImage
      heroEyebrow
      heroHeadingLine1
      heroHeadingLine2
      heroBody
      storyImage
      storyEyebrow
      storyHeading
      storyBody1
      storyBody2
      pullQuote
      pullQuoteBy
      philosophyEyebrow
      philosophyHeading
      philosophySub
      ctaEyebrow
      ctaHeading
      ctaBody
      travellersDisplay
    }
    aboutStats {
      totalTours
      totalCountries
      yearsOperating
      travellers
    }
    companyValues {
      id
      icon
      title
      description
      sortOrder
    }
    teamMembers {
      id
      name
      role
      bio
      photo
      sortOrder
    }
    companyMilestones {
      id
      year
      title
      description
      sortOrder
    }
    pressPartners {
      id
      name
      logoUrl
      sortOrder
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// NEWSLETTER MUTATION
// ─────────────────────────────────────────────────────────────────────────────

export const SUBSCRIBE_NEWSLETTER = gql`
  mutation SubscribeNewsletter($email: String!) {
    subscribeNewsletter(email: $email) {
      success
      message
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT ENQUIRY MUTATION
// ─────────────────────────────────────────────────────────────────────────────

export const SUBMIT_CONTACT_ENQUIRY = gql`
  mutation SubmitContactEnquiry(
    $name:      String!
    $email:     String!
    $phone:     String
    $subject:   String
    $travelers: String
    $message:   String!
  ) {
    submitContactEnquiry(
      name:      $name
      email:     $email
      phone:     $phone
      subject:   $subject
      travelers: $travelers
      message:   $message
    ) {
      success
      message
      enquiryId
    }
  }
`;